import React, { useEffect, useState } from "react";
import styles from "./PhoneForm.module.css";
import { Controller, useForm } from "react-hook-form";
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import useGeoLocation from "@/services/useGeoLocation";
import validator from "validator";
import Image from "next/image";
import OTP from "../OTP/OTP";
import UpdatePhone from "../UpdatePhone/UpdatePhone";
import { removeCountryCode } from "@/utils/cookieutils";
import axios from "axios";
import Link from "next/link";
import { toast } from "react-hot-toast";

type FormData = {
  phone: string;
};

const PhoneForm = ({ resetStep, setResetStep, onHide, onOtpSuccess }: any) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormData>();

  const geoLocationData: any = useGeoLocation();
  const [step, setStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [countryCode, setCountryCode] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  const [isValidPhoneNumberData, setIsValidPhoneNumberData] = useState(false);

  const BASE_URL = process.env.UNIFIED_RMS_BASE_URL;

  useEffect(() => {
    if (geoLocationData?.countryCode) {
      const callingCode = getCountryCallingCode(geoLocationData.countryCode);
      setCountryCode(callingCode);
    }
  }, [geoLocationData?.countryCode]);
  const handleCountryCodeChange = (country: any) => {
    if (country) {
      const callingCode = getCountryCallingCode(country);
      setCountryCode(callingCode);
    }
  };
  const mobileWithoutCode = removeCountryCode(countryCode, mobileNumber);

  useEffect(() => {
    if (resetStep) {
      setStep(1);
      setMobileNumber("");
      setResetStep(false);
      setResendDisabled(false);
    }
  }, [resetStep, setResetStep]);

  const onSubmit = async () => {
    const trimmedMobileNumber = mobileWithoutCode.trim();
    if (!mobileNumber || !isValidPhoneNumber(mobileNumber)) {
      setIsValidPhoneNumberData(false);
      return;
    }
    setIsValidPhoneNumberData(true);
    const payload = {
      mobileCountryCode: countryCode,
      mobileNumber: trimmedMobileNumber,
    };
    if (payload) {
      try {
        setIsLoading(true);
        const response = await axios.post(`${BASE_URL}auth/login`, payload);
        if (response?.data?.statusCode == 200) {
          toast.success("OTP sent successfully 🎉");
          setStep(2);
        } else if (response?.data?.statusCode == 400) {
          toast.error(response?.data?.messages);
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          toast.error(err.response.data?.message);
        } else {
          toast.error("Network error. Please check your connection.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlesendOTP = async () => {
    const trimmedMobileNumber = mobileWithoutCode.trim();
    const payload = {
      mobileCountryCode: countryCode,
      mobileNumber: trimmedMobileNumber,
    };
    if (payload) {
      try {
        setIsLoading(true);
        const response = await axios.post(`${BASE_URL}auth/login`, payload);
        if (response?.data?.statusCode == 200) {
          toast.success("OTP sent successfully 🎉");
          setStep(2);
        } else if (response?.data?.statusCode == 400) {
          toast.error(response?.data?.messages);
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          toast.error(err.response.data?.message);
          setResendDisabled(true);
        } else {
          toast.error("Network error. Please check your connection.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOtpSuccessLocal = (
    tokenDetails: any,
    userStatus: any,
    studentCode: any
  ) => {
    onOtpSuccess(tokenDetails, userStatus, studentCode);
  };

  return (
    <>
      <div className={styles.topContainer}>
        <div className={styles.isMobile}>
          <Image
            src={"/assets/regLogo.png"}
            alt="log"
            title="log"
            width={243}
            height={50}
          />
        </div>
        {step === 1 && (
          <>
            <div className={styles.textContainer}>
              <p className={styles.welcomeText}>Welcome to Regenesys!</p>
              <span className={styles.subtext}>
                Embark on your journey of excellence with us.
              </span>
            </div>

            <form
              action=""
              onSubmit={handleSubmit(onSubmit)}
              className={styles.formWidth}
            >
              <label htmlFor="" className={styles.label}>
                Enter your phone number
              </label>
              <div
                className={`${styles.fieldInPut} ${styles.formContainer} ${
                  isFocused ? styles.inputFocused : ""
                }`}
              >
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: "Phone number is required",
                    validate: (value) =>
                      value ? true : "Phone number is required",
                  }}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      className={styles.PhoneInput}
                      autoComplete="off"
                      value={mobileNumber}
                      defaultCountry={geoLocationData?.countryCode}
                      international
                      countryCallingCodeEditable={false}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onChange={(value) => {
                        const phoneNumber = value ? value.toString() : "";
                        const isValid = validator.isMobilePhone(
                          phoneNumber || ""
                        );
                        field.onChange(phoneNumber);
                        setMobileNumber(phoneNumber);

                        if (phoneNumber.replace(/[^0-9]/g, "").length >= 4) {
                          if (isValid) {
                            setMobileNumber(phoneNumber);
                            setPhoneNumberError("");
                          } else {
                            setPhoneNumberError("Valid phone number required");
                          }
                        } else {
                          setPhoneNumberError("");
                        }
                      }}
                      onCountryChange={handleCountryCodeChange}
                    />
                  )}
                />
              </div>
              <div>
                {phoneNumberError ? (
                  <small className={styles.error}>{phoneNumberError}</small>
                ) : (
                  (mobileNumber.length === 0
                    ? !phoneNumberError
                    : phoneNumberError || isValidPhoneNumberData) &&
                  errors?.phone && (
                    <small className={styles.error}>
                      {errors?.phone?.message}
                    </small>
                  )
                )}
              </div>
              <hr className={styles.bottomHr} />
              <button
                className={`${styles.verifyBtn} ${
                  isLoading ? styles.disabled : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send OTP"}
              </button>

              <div>
                <p className={styles.smallText}>
                  For assistance, please{" "}
                  <Link
                    title="reach out to us"
                    href={
                      "/contact-us-for-admissions-and-other-queries-regenesys"
                    }
                  >
                    <span>reach out to us</span>
                  </Link>
                </p>
              </div>
            </form>
          </>
        )}
        {step === 2 && (
          <div>
            <OTP
              countryCode={countryCode}
              mobileNumber={mobileNumber}
              setStep={setStep}
              onClose={onHide}
              handlesendOTP={handlesendOTP}
              resendDisabled={resendDisabled}
              onOtpSuccess={handleOtpSuccessLocal}
            />
          </div>
        )}

        {step === 3 && (
          <UpdatePhone
            setStep={setStep}
            mobileNumber={mobileNumber}
            setMobileNumber={setMobileNumber}
            handleCountryCodeChange={handleCountryCodeChange}
            handlesendOTP={handlesendOTP}
          />
        )}
      </div>
    </>
  );
};

export default PhoneForm;
